import type { FC } from 'react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PiCalendarDotsBold } from 'react-icons/pi';
import type { PopoverProps } from '@mui/material';
import {
  Box,
  Button,
  Divider,
  Popover,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';

import type { RequiredDateRange } from 'src/components/myFollowUp/MyFollowUpPage/Sidebar/helper';
import {
  areDateRangesEqual,
  presetDateRanges,
} from 'src/components/myFollowUp/MyFollowUpPage/Sidebar/helper';
import type { DateRange } from 'src/components/ui/DateRangePicker';
import { DateRangePicker } from 'src/components/ui/DateRangePicker';

export interface DateFilterModalProps extends Pick<PopoverProps, 'anchorEl'> {
  initialDateRange: RequiredDateRange;
  minDate?: Date;
  onClose: () => void;
  onSubmit: (dateRange: RequiredDateRange) => void;
  open: boolean;
}

const DateFilterModal: FC<DateFilterModalProps> = ({
  anchorEl,
  initialDateRange,
  minDate,
  onClose,
  onSubmit,
  open,
}) => {
  const { t } = useTranslation(['followUp', 'common']);
  const [dateRange, setDateRange] =
    useState<RequiredDateRange>(initialDateRange);
  const [pickerKey, setPickerKey] = useState(0);

  const validatedPresetDateRanges = useMemo(
    () =>
      presetDateRanges.map((preset) =>
        minDate && preset.startDate < minDate
          ? { ...preset, startDate: minDate }
          : preset,
      ),
    [minDate],
  );

  const onChange = (newDateRange: DateRange) => {
    if (newDateRange.startDate && newDateRange.endDate) {
      setDateRange(newDateRange as RequiredDateRange);
    }
  };

  const onSelectPreset = (preset: RequiredDateRange) => {
    setDateRange(preset);
    // The daterange picker component does not expose a method to set the date range. Instead, we
    // can force a re-render by changing the key of the component
    setPickerKey((prevKey) => prevKey + 1);
  };

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      slotProps={{
        paper: {
          sx: {
            'borderRadius': 2,
            'boxShadow': ' 0px 4px 23px 0px rgba(0, 0, 0, 0.08)',
            // override the background color of the date range picker
            '& .MuiGrid2-root': {
              backgroundColor: 'white!important',
            },
          },
        },
      }}
    >
      <Box sx={{ p: 2.5 }}>
        <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center' }}>
          <Typography
            sx={{
              fontWeight: 600,
              display: 'flex',
              gap: 1,
              alignItems: 'center',
            }}
          >
            <PiCalendarDotsBold height={18} />
            {t('followUp:dateFilter.selectRange')}
          </Typography>
          <Typography color="textSecondary" sx={{ fontWeight: 600 }}>
            {t('common:or')}
          </Typography>
          <ToggleButtonGroup>
            {validatedPresetDateRanges.map((preset) => (
              <ToggleButton
                key={preset.label}
                value={preset}
                selected={areDateRangesEqual(dateRange, preset)}
                onClick={() => onSelectPreset(preset)}
                color="primary"
              >
                {t(`followUp:dateFilter.presets.${preset.label}`)}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Box>

        <Divider sx={{ mb: 2 }} />

        <DateRangePicker
          initialDateRange={dateRange}
          onChange={onChange}
          key={pickerKey}
          minDate={minDate}
        />

        <Box sx={{ justifyContent: 'flex-end', display: 'flex', gap: 3 }}>
          <Button
            variant="text"
            color="primary"
            onClick={() => onSelectPreset(presetDateRanges[0])}
          >
            {t('common:reset')}
          </Button>
          <Button
            variant="cta"
            color="secondary"
            onClick={() => onSubmit(dateRange)}
          >
            {t('followUp:dateFilter.applyRange')}
          </Button>
        </Box>
      </Box>
    </Popover>
  );
};

export default DateFilterModal;
